import { Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  DialogCloseResult,
  DialogContentBase,
  DialogRef,
  DialogService,
  DialogsModule,
  WindowModule,
  WindowRef
} from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { Observable, Subscription } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import { faArrowDown, faArrowUp, faEdit, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { CasiCloseOptions, FormModes } from '../../common/constants.lib';
import {
  Evaluation,
  EvaluationRatingScale,
  EvaluationRatingScaleNew,
  EvaluationRatingScaleType
} from '../../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { EvaluationRatingScaleService } from '../evaluation-rating-scale.service';
import {
  EvaluationRatingScaleAddComponent
} from '../evaluation-rating-scale-add.component/evaluation-rating-scale-add.component';
import { setTitle } from '../../common/utils.lib';

@Component({
  selector: 'casi-evaluation-rating-scale-list-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './evaluation-rating-scale-list-win.html',
  styleUrl: './evaluation-rating-scale-list-win.scss'
})
export class EvaluationRatingScaleListWin implements OnInit, OnDestroy {
  private _evaluation: Evaluation | undefined;

  @Input() title: string = 'Title Not Set';

  @Input({ required: true }) set evaluation(val: Evaluation) {
    this._evaluation = val;
  }
  get evaluation(): Evaluation | undefined {
    return this._evaluation;
  };

  @Output() onClose = new EventEmitter<void>();


  private _windowRef: WindowRef | undefined;

  public view: Observable<EvaluationRatingScale[]> = new Observable<EvaluationRatingScale[]>();
  public rawData: EvaluationRatingScale[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: EvaluationRatingScale[] = filterBy(this.rawData, this.filter);

  public currentItem: EvaluationRatingScaleType = EvaluationRatingScaleNew.create(this.evaluation);

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  public editWinOpen: Boolean = false;
  public formMode: FormModes = FormModes.NOTSET;
  private _subs: Subscription[] = [];


  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: EvaluationRatingScaleService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private toastr: ToastrService
  ) {
    faIcons.addIcons(
      faPlusCircle, faEdit, faArrowUp, faArrowDown
    );
  }

  public sort: SortDescriptor[] = [
    { field: "rating_scale_value", dir: "asc" },
    { field: "rating_scale_description", dir: "asc" },
  ];

  //#region Ng Handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    if (this.evaluation)
      this.dataService.query(this.evaluation.id);

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid handlers

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region Public methods

  setActive(dataItem: any): void {
    dataItem.active = !dataItem.active;
    this.logger.debug('setActive:dataItem: ', dataItem);
    this.currentItem = EvaluationRatingScale.create(dataItem);
    this.saveActive(this.currentItem);
  }

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
    //  this.dialog.close({ text: CasiCloseOptions.Cancelled });
  }

  public onAddClick(): void {
    this.logger.debug("onAddClick...");
    this.currentItem = EvaluationRatingScaleNew.create(this.evaluation);
    this.formMode = FormModes.NEW;
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = EvaluationRatingScale.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  //#endregion

  //#region Private methods

  private openAddEditDialog(mode: FormModes, dataItem: EvaluationRatingScaleType): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: EvaluationRatingScaleAddComponent,
      minWidth: 320,
      width: 600,
    });

    const contentRef = dialogRef.content.instance as EvaluationRatingScaleAddComponent;
    contentRef.title = setTitle(mode, 'EVALUATION RATING SCALE');
    contentRef.evaluationRatingScale = dataItem;
    contentRef.mode = mode;
    contentRef.existingData = this.rawData;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Saved) {
        this.logger.debug("openAddEditDialog:saved... ", contentRef.editForm.value);
        this.handleClose(contentRef.editForm.value, mode);
      }
    });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    this.save(data, mode);
  }

  private save(evaluationRatingScale: any, mode: FormModes): void {
    this.logger.debug('save...', evaluationRatingScale, mode);
    if (mode === FormModes.EDIT) {
      this.dataService.update(evaluationRatingScale.id, evaluationRatingScale)
        .then(resp => {
          this.logger.debug('save:update:resp: ', resp);
          this.notify("Evaluation Rating Scale Updated");
          this.closeWindowRef();
          if (this._evaluation)
            this.dataService.query(this._evaluation.id);
        })
        .catch(err => {
          this.logger.error('save:update:err: ', err);
          this.notify("Error Updating Evaluation Rating Scale", true);
        });
    } else {
      this.dataService.add(evaluationRatingScale)
        .then(resp => {
          this.logger.debug('save:add:resp: ', resp);
          this.notify("Evaluation Rating Scale Added");
          this.closeWindowRef();
          if (this._evaluation)
            this.dataService.query(this._evaluation.id);
        })
        .catch(err => {
          this.logger.error('save:add:err: ', err);
          this.notify("Error Creating Evaluation Rating Scale", true);
        });
    }
  }

  private saveActive(evaluationRatingScale: any): void {
    this.logger.debug('saveActive...', evaluationRatingScale);
    this.dataService.update(evaluationRatingScale.id, evaluationRatingScale)
      .then(resp => {
        this.logger.debug('saveActive:update:resp: ', resp);
        this.notify(evaluationRatingScale.active ? "Evaluation Rating Scale Activated" : "Evaluation Rating Scale Deactivated");
        this.closeWindowRef();
        if (this._evaluation)
          this.dataService.query(this._evaluation.id);
      })
      .catch(err => {
        this.logger.error('saveActive:update:err: ', err);
        this.notify("Error Updating Evaluation Rating Scale", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  //#endregion
  
}

